import {
  logSlowApiRequestSettledForSupport,
  logSlowApiRequestStartedForSupport,
} from "./slowRequestSupportLog";

/**
 * Shared client-side state for "the hub is degraded" messaging:
 *
 * - Auth-service outage: the API answered 503 `auth_service_unavailable`
 *   (Supabase Auth unreachable upstream). The session is fine; the hub retries.
 * - Slow requests: an `apiFetch` call has been in flight longer than
 *   {@link SLOW_API_REQUEST_THRESHOLD_MS} — the user should see progress
 *   messaging instead of a bare spinner.
 *
 * Plain module store (no React import) so `apiFetch` can report from anywhere;
 * components subscribe via `useSyncExternalStore`.
 */

export interface HubAuthOutageState {
  /** When the first 503 of this outage was reported. */
  sinceMs: number;
  /** When the most recent 503 was reported (drives auto-expiry). */
  lastSignalMs: number;
  /** Server-suggested retry spacing (Retry-After). */
  retryAfterSeconds: number;
}

export interface HubSlowRequestState {
  id: number;
  path: string;
  startedAtMs: number;
  method: string;
  /** Matches `X-Hub-Client-Request-Id` on the in-flight fetch. */
  clientRequestId?: string;
}

export interface HubBackendDegradedState {
  authOutage: HubAuthOutageState | null;
  slowRequests: readonly HubSlowRequestState[];
}

/** A single request slower than this gets "still working" messaging. */
export const SLOW_API_REQUEST_THRESHOLD_MS = 8_000;

/** Hide the auth-outage banner when no new 503 arrived for this long. */
export const AUTH_OUTAGE_SIGNAL_EXPIRY_MS = 90_000;

let state: HubBackendDegradedState = { authOutage: null, slowRequests: [] };
const listeners = new Set<() => void>();
let nextSlowRequestId = 1;

function emit(): void {
  for (const listener of listeners) {
    listener();
  }
}

export function subscribeHubBackendDegraded(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getHubBackendDegradedState(): HubBackendDegradedState {
  return state;
}

export function reportAuthServiceDegraded(retryAfterSeconds: number, nowMs = Date.now()): void {
  state = {
    ...state,
    authOutage: {
      sinceMs: state.authOutage?.sinceMs ?? nowMs,
      lastSignalMs: nowMs,
      retryAfterSeconds,
    },
  };
  emit();
}

export function reportAuthServiceRecovered(): void {
  if (state.authOutage === null) {
    return;
  }
  state = { ...state, authOutage: null };
  emit();
}

export function isAuthServiceDegraded(): boolean {
  return state.authOutage !== null;
}

/**
 * Record a request that has exceeded the slow threshold.
 * @param startedAtMs Wall time when the fetch began (not when this report fires).
 */
export function reportSlowApiRequestStarted(
  path: string,
  startedAtMs = Date.now(),
  method = "GET",
  clientRequestId?: string,
): number {
  const id = nextSlowRequestId;
  nextSlowRequestId += 1;
  const nowMs = Date.now();
  logSlowApiRequestStartedForSupport(
    path,
    startedAtMs,
    method,
    SLOW_API_REQUEST_THRESHOLD_MS,
    nowMs,
    clientRequestId,
  );
  state = {
    ...state,
    slowRequests: [...state.slowRequests, { id, path, startedAtMs, method, clientRequestId }],
  };
  emit();
  return id;
}

export function reportSlowApiRequestSettled(id: number): void {
  const request = state.slowRequests.find((entry) => entry.id === id);
  if (!request) {
    return;
  }
  logSlowApiRequestSettledForSupport(
    request.path,
    request.startedAtMs,
    request.method,
    SLOW_API_REQUEST_THRESHOLD_MS,
    Date.now(),
    request.clientRequestId,
  );
  state = {
    ...state,
    slowRequests: state.slowRequests.filter((entry) => entry.id !== id),
  };
  emit();
}

/** True while the outage is fresh enough to show (guards against a stuck banner). */
export function authOutageActive(current: HubBackendDegradedState, nowMs: number): boolean {
  if (current.authOutage === null) {
    return false;
  }
  return nowMs - current.authOutage.lastSignalMs < AUTH_OUTAGE_SIGNAL_EXPIRY_MS;
}

/** Whole seconds until the next automatic retry (floors at 0 = "retrying now"). */
export function secondsUntilAuthOutageRetry(current: HubBackendDegradedState, nowMs: number): number {
  if (current.authOutage === null) {
    return 0;
  }
  const retryAtMs = current.authOutage.lastSignalMs + current.authOutage.retryAfterSeconds * 1_000;
  return Math.max(0, Math.ceil((retryAtMs - nowMs) / 1_000));
}

/** Elapsed seconds of the longest-running slow request, or null when none. */
export function oldestSlowRequestElapsedSeconds(
  current: HubBackendDegradedState,
  nowMs: number,
): number | null {
  if (current.slowRequests.length === 0) {
    return null;
  }
  const oldest = current.slowRequests.reduce((a, b) => (a.startedAtMs <= b.startedAtMs ? a : b));
  return Math.max(0, Math.floor((nowMs - oldest.startedAtMs) / 1_000));
}

export function authOutageBannerText(secondsUntilRetry: number): string {
  if (secondsUntilRetry <= 0) {
    return "Sign-in verification is temporarily unavailable — retrying now. You do not need to sign out.";
  }
  return `Sign-in verification is temporarily unavailable — retrying automatically in ${secondsUntilRetry}s. You do not need to sign out.`;
}

export function slowRequestBannerText(elapsedSeconds: number, count: number): string {
  const subject = count > 1 ? `${count} requests are` : "A request is";
  return `${subject} taking longer than usual (${elapsedSeconds}s so far). The hub is still working on it.`;
}

/** Test-only reset so unit tests do not leak state between cases. */
export function resetHubBackendDegradedStateForTests(): void {
  state = { authOutage: null, slowRequests: [] };
  nextSlowRequestId = 1;
}

/**
 * Start a slow-request watch for one apiFetch. finish() must run in a finally.
 * If the request settles in the same turn as the threshold timer, do not leave a leaked banner row.
 */
export function beginSlowApiRequestWatch(
  path: string,
  fetchStartedAtMs: number,
  options: { skip?: boolean; now?: typeof globalThis; method?: string; clientRequestId?: string } = {},
): { finish: () => void } {
  const host = options.now ?? globalThis;
  const method = options.method?.trim().toUpperCase() || "GET";
  let finished = false;
  let slowRequestId: number | null = null;
  const skip = options.skip === true;
  const timer = skip
    ? undefined
    : host.setTimeout(() => {
        if (finished) {
          return;
        }
        slowRequestId = reportSlowApiRequestStarted(
          path,
          fetchStartedAtMs,
          method,
          options.clientRequestId,
        );
      }, SLOW_API_REQUEST_THRESHOLD_MS);

  return {
    finish: () => {
      finished = true;
      if (timer !== undefined) {
        host.clearTimeout(timer);
      }
      if (slowRequestId !== null) {
        reportSlowApiRequestSettled(slowRequestId);
        slowRequestId = null;
      }
    },
  };
}
